(function () {
  'use strict';

  class YouTubeVideoCarousel {
    constructor(element) {
      this.element = element;
      this.track = element.querySelector(
        '.youtube-video-carousel__track'
      );

      this.previousButton = element.querySelector(
        '.youtube-video-carousel__button--prev'
      );

      this.nextButton = element.querySelector(
        '.youtube-video-carousel__button--next'
      );

      this.loadMoreButton = element.querySelector(
        '[data-load-more]'
      );

      // Keep track of the currently playing video.
      this.activeVideo = null;

      this.init();
    }

    init() {
      this.bindEvents();
      this.updateNavigation();
    }

    bindEvents() {
      if (this.previousButton) {
        this.previousButton.addEventListener(
          'click',
          () => this.scrollPrevious()
        );
      }

      if (this.nextButton) {
        this.nextButton.addEventListener(
          'click',
          () => this.scrollNext()
        );
      }

      if (this.track) {
        this.track.addEventListener(
          'scroll',
          () => this.updateNavigation(),
          {
            passive: true
          }
        );
      }

      this.bindVideoButtons();
      this.bindLoadMore();

      window.addEventListener(
        'resize',
        () => this.updateNavigation()
      );
    }

    bindVideoButtons() {
      const thumbnails =
        this.element.querySelectorAll(
          '.youtube-video-carousel__thumbnail'
        );

      thumbnails.forEach((thumbnail) => {
        this.bindSingleVideoButton(thumbnail);
      });
    }

    /*
     * Split out so newly-revealed thumbnails
     * (from Load More) can be wired up the
     * same way as the ones bound on init.
     */
    bindSingleVideoButton(thumbnail) {
      if (thumbnail.dataset.videoButtonBound === 'true') {
        return;
      }

      thumbnail.dataset.videoButtonBound = 'true';

      thumbnail.addEventListener(
        'click',
        () => {
          const videoId =
            thumbnail.dataset.videoId;

          if (!videoId) {
            return;
          }

          this.playVideo(
            thumbnail,
            videoId
          );
        }
      );
    }

    playVideo(thumbnail, videoId) {
      /*
       * Stop the currently playing video
       * before starting a new one.
       */
      if (this.activeVideo) {
        this.stopActiveVideo();
      }

      /*
       * Save the original thumbnail HTML
       * so we can restore it later.
       */
      const originalHTML =
        thumbnail.outerHTML;

      thumbnail.dataset.originalHtml =
        originalHTML;

      /*
       * Create YouTube iframe.
       */
      const iframe =
        document.createElement('iframe');

      iframe.className =
        'youtube-video-carousel__iframe';

      iframe.src =
        `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;

      iframe.title =
        thumbnail.getAttribute('aria-label') ||
        'YouTube video';

      iframe.loading = 'lazy';

      iframe.allow =
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';

      iframe.allowFullscreen = true;

      /*
       * Store reference to the active video.
       */
      this.activeVideo = {
        iframe: iframe,
        thumbnail: thumbnail,
        originalHTML: originalHTML
      };

      /*
       * Replace thumbnail with iframe.
       */
      thumbnail.replaceWith(iframe);
    }

    stopActiveVideo() {
      if (!this.activeVideo) {
        return;
      }

      const {
        iframe,
        originalHTML
      } = this.activeVideo;

      /*
       * Remove iframe from the DOM.
       * This stops YouTube playback.
       */
      if (iframe && iframe.parentNode) {
        const wrapper =
          document.createElement('div');

        wrapper.innerHTML =
          originalHTML.trim();

        const thumbnail =
          wrapper.firstElementChild;

        /*
         * The captured HTML was snapshotted
         * after binding, so it already carries
         * the "bound" flag. Clear it so the
         * fresh element actually gets a click
         * listener attached below — otherwise
         * this thumbnail can never be replayed.
         */
        delete thumbnail.dataset.videoButtonBound;

        iframe.replaceWith(thumbnail);

        /*
         * Re-bind click event because the
         * thumbnail is a newly created element.
         */
        this.bindSingleVideoButton(thumbnail);
      }

      this.activeVideo = null;
    }

    scrollNext() {
      this.track.scrollBy({
        left: this.track.clientWidth,
        behavior: 'smooth'
      });
    }

    scrollPrevious() {
      this.track.scrollBy({
        left: -this.track.clientWidth,
        behavior: 'smooth'
      });
    }

    updateNavigation() {
      if (
        !this.previousButton ||
        !this.nextButton
      ) {
        return;
      }

      const maxScroll =
        this.track.scrollWidth -
        this.track.clientWidth;

      const currentScroll =
        this.track.scrollLeft;

      this.previousButton.disabled =
        currentScroll <= 5;

      this.nextButton.disabled =
        currentScroll >= maxScroll - 5;
    }

    /*
     * ---------------------------------------------
     * LOAD MORE (episode grid)
     * ---------------------------------------------
     * Cards beyond the initial batch are rendered
     * with the --hidden class server-side, and the
     * grid's data-batch-size sets how many reveal
     * per click.
     *
     * The button toggles between two modes:
     *  - "expand": reveals the next batch. If that
     *    batch empties the hidden pool, the button
     *    switches to "collapse" mode.
     *  - "collapse": re-hides every extra card and
     *    switches back to "expand" mode.
     */

    bindLoadMore() {
      if (!this.loadMoreButton) {
        return;
      }

      const grid = this.element.querySelector(
        '.youtube-video-carousel__grid'
      );

      if (!grid) {
        return;
      }

      this.loadMoreGrid = grid;

      this.loadMoreLabelEl =
        this.loadMoreButton.querySelector(
          '[data-load-more-label]'
        );

      this.batchSize = this.getBatchSize();

      /*
       * Cards beyond the initial batch, captured once.
       * Their --hidden class is toggled on/off rather
       * than the elements being re-queried each time.
       */
      this.extraCards =
        Array.prototype.slice.call(
          grid.querySelectorAll(
            '.youtube-video-carousel__card--hidden'
          )
        );

      this.updateLoadMoreLabel();

      this.loadMoreButton.addEventListener(
        'click',
        () => this.handleLoadMoreClick()
      );
    }

    getBatchSize() {
      const size = parseInt(
        this.loadMoreGrid.dataset.batchSize,
        10
      );

      return Number.isFinite(size) && size > 0
        ? size
        : 10;
    }

    getHiddenExtraCards() {
      return this.extraCards.filter((card) =>
        card.classList.contains(
          'youtube-video-carousel__card--hidden'
        )
      );
    }

    handleLoadMoreClick() {
      if (this.loadMoreMode === 'collapse') {
        this.collapseExtraCards();
      } else {
        this.revealNextBatch();
      }
    }

    revealNextBatch() {
      const hiddenCards =
        this.getHiddenExtraCards();

      if (!hiddenCards.length) {
        this.updateLoadMoreLabel();
        return;
      }

      const nextBatch =
        hiddenCards.slice(0, this.batchSize);

      /*
       * Brief loading state on the button so
       * the reveal feels intentional rather than
       * an instant, jarring pop-in.
       */
      this.loadMoreButton.classList.add(
        'is-loading'
      );

      this.loadMoreButton.disabled = true;

      window.setTimeout(
        () => {
          nextBatch.forEach((card) => {
            card.classList.remove(
              'youtube-video-carousel__card--hidden'
            );

            const thumbnail =
              card.querySelector(
                '.youtube-video-carousel__thumbnail'
              );

            if (thumbnail) {
              this.bindSingleVideoButton(
                thumbnail
              );
            }
          });

          this.loadMoreButton.classList.remove(
            'is-loading'
          );

          this.loadMoreButton.disabled = false;

          this.updateLoadMoreLabel();
        },
        250
      );
    }

    collapseExtraCards() {
      this.extraCards.forEach((card) => {
        card.classList.add(
          'youtube-video-carousel__card--hidden'
        );
      });

      this.updateLoadMoreLabel();

      this.loadMoreGrid.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }

    updateLoadMoreLabel() {
      if (!this.loadMoreButton) {
        return;
      }

      const hiddenCount =
        this.getHiddenExtraCards().length;

      if (hiddenCount === 0) {
        this.loadMoreMode = 'collapse';

        this.setLoadMoreLabel(
          this.loadMoreButton.dataset
            .labelCollapse
        );

        return;
      }

      this.loadMoreMode = 'expand';

      const revealCount = Math.min(
        this.batchSize,
        hiddenCount
      );

      const isFinalBatch =
        revealCount === hiddenCount;

      const template = isFinalBatch
        ? this.loadMoreButton.dataset
          .labelFinalTemplate
        : this.loadMoreButton.dataset
          .labelTemplate;

      this.setLoadMoreLabel(
        (template || '').replace(
          '{count}',
          revealCount
        )
      );
    }

    setLoadMoreLabel(text) {
      if (this.loadMoreLabelEl) {
        this.loadMoreLabelEl.textContent = text;
      }
    }
  }

  function initCarousels(container = document) {
    const carousels =
      container.querySelectorAll(
        '.youtube-video-carousel'
      );

    carousels.forEach((carousel) => {
      if (
        carousel.dataset.youtubeCarouselInitialized ===
        'true'
      ) {
        return;
      }

      carousel.dataset.youtubeCarouselInitialized =
        'true';

      new YouTubeVideoCarousel(carousel);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initCarousels();
      }
    );
  } else {
    initCarousels();
  }

  /*
   * Shopify Theme Editor support.
   */
  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initCarousels(event.target);
    }
  );
})();